import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/student-book-report/StudentBookReportAction";
import {
  ExportButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../../_helpers/Helpers";
import {
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { useBookAndPageNumberUIContext } from "./BookAndPageNumberUIContext";
import { BookAndPageNumberFilter } from "./book-and-page-number-filter/BookAndPageNumberFilter";
import { BookAndPageNumberTable } from "./book-and-page-number-table/BookAndPageNumberTable";

export function BookAndPageNumberCard() {
  // Book And Page Number UI Context
  const bookAndPageNumberUIContext = useBookAndPageNumberUIContext();
  const bookAndPageNumberUIProps = useMemo(() => {
    return {
      queryParams: bookAndPageNumberUIContext.queryParams
    };
  }, [bookAndPageNumberUIContext]);
    
  // Getting curret state of Book And Page Number from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.studentBookReport }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // Book And Page Number Redux state
  const dispatch = useDispatch(); 

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğrenci Kitap ve Sayfa Sayısı Raporunu Excele Aktarır */
  const exportBookAndPageNumberToExcel = () => {
    dispatch(actions.getAllBookAndPageNumberReport(bookAndPageNumberUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [25, 25, 30, 25, 20];
      const columns = ["Şube", "Öğrenci Numarası", "Adı Soyadı", "Kitap Sayısı", "Sayfa Sayısı"];
      entities.forEach(entity => rows.push([
        entity.section,
        entity.studentNumber,
        entity.studentName,
        entity.bookCount,
        entity.pageNumberCount
      ]));
      const filename = bookAndPageNumberUIProps.queryParams.term + " - " + (bookAndPageNumberUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Kitap ve Sayfa Sayısı Raporu";
      const sheetname = "Kitap ve Sayfa Sayısı Raporu"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Öğrenci Kitap ve Sayfa Sayısı Raporunu PDF e Aktarır */
  const exportBookAndPageNumberToPDF = () => {
    dispatch(actions.getAllBookAndPageNumberReport(bookAndPageNumberUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Öğrenci Numarası",
          rows: entities.map(entity => entity.studentNumber)
        },
        {
          header: "Adı Soyadı",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Kitap Sayısı",
          rows: entities.map(entity => entity.bookCount)
        },
        {
          header: "Sayfa Sayısı",
          rows: entities.map(entity => entity.pageNumberCount)
        }
      ];
  
      const filename = bookAndPageNumberUIProps.queryParams.term + " - " + (bookAndPageNumberUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Kitap ve Sayfa Sayısı Raporu";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Öğrenci Kitap ve Sayfa Sayısı Raporunu Yazdırma Penceresini Açar */
  const printBookAndPageNumber = () => {
    dispatch(actions.getAllBookAndPageNumberReport(bookAndPageNumberUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = bookAndPageNumberUIProps.queryParams.term + " - " + (bookAndPageNumberUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Kitap ve Sayfa Sayısı Raporu";
      const properties = ["Şube", "Öğrenci Numarası", "Adı Soyadı", "Kitap Adı", "Sayfa Sayısı"];

      entities.forEach(entity => {
        data.push({
          "Şube": entity.section,
          "Öğrenci Numarası": entity.studentNumber,
          "Adı Soyadı": entity.studentName,
          "Kitap Sayısı": entity.bookCount,
          "Sayfa Sayısı": entity.pageNumberCount
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Öğrenci Kitap ve Sayfa Sayısı Raporunu Panoya Kopyalar */
  const copyBookAndPageNumber = () => {
    dispatch(actions.getAllBookAndPageNumberReport(bookAndPageNumberUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = bookAndPageNumberUIProps.queryParams.term + " - " + (bookAndPageNumberUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Kitap ve Sayfa Sayısı Raporu";
      const columns = ["Şube", "Öğrenci Numarası", "Adı Soyadı", "Kitap Sayısı", "Sayfa Sayısı"];
      entities.forEach(entity => rows.push([
        entity.section,
        entity.studentNumber,
        entity.studentName,
        entity.bookCount,
        entity.pageNumberCount
      ]));
      copy({ header, columns, rows });
    });
  }

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Kitap ve Sayfa Sayısı">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>
            <ExportButton
              exportToExcel={exportBookAndPageNumberToExcel}
              exportToPDF={exportBookAndPageNumberToPDF}
              print={printBookAndPageNumber}
              copy={copyBookAndPageNumber}
            />
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <BookAndPageNumberFilter />
        <BookAndPageNumberTable />
      </CardBody>
    </Card>
  );
}